import React from "react";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import type {
  TimelineCustomRange,
  ZoomLevel,
} from "../../../utils/chart/timeline-range.utils";
import {
  parseDateInputValue,
  toDateInputValue,
} from "../../../utils/chart/timeline-range.utils";

export type { ZoomLevel, TimelineCustomRange };

export interface TimelineZoomProps {
  zoomLevel: ZoomLevel;
  onZoomChange: (level: ZoomLevel) => void;
  customRange?: TimelineCustomRange | null;
  onCustomRangeChange?: (range: TimelineCustomRange) => void;
  onPanLeft?: () => void;
  onPanRight?: () => void;
  onGoToToday?: () => void;
  onGoToStart?: () => void;
  onGoToEnd?: () => void;
  startDateLabel?: string;
  endDateLabel?: string;
  className?: string;
}

const TimelineZoom: React.FC<TimelineZoomProps> = ({
  zoomLevel,
  onZoomChange,
  customRange,
  onCustomRangeChange,
  onPanLeft,
  onPanRight,
  onGoToToday,
  onGoToStart,
  onGoToEnd,
  startDateLabel = "Start",
  endDateLabel = "End",
  className = "",
}) => {
  const zoomOptions = [
    { value: "6months" as const, label: "6M" },
    { value: "18months" as const, label: "18M" },
    { value: "1year" as const, label: "1Y" },
    { value: "3years" as const, label: "3Y" },
    { value: "5years" as const, label: "5Y" },
    { value: "10years" as const, label: "10Y" },
    { value: "custom" as const, label: "Custom" },
  ];

  const shell = "rounded-[14px] border border-white/10 bg-surface-elevated";
  const btn =
    "p-1.5 text-white/60 transition-colors hover:bg-surface/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-40";

  const handleCustomDateChange = (
    field: "startDate" | "endDate",
    value: string,
  ) => {
    if (!onCustomRangeChange || !customRange) return;
    const parsed = parseDateInputValue(value);
    if (!parsed) return;

    const next: TimelineCustomRange = {
      ...customRange,
      [field]: parsed,
    };

    if (next.endDate.getTime() < next.startDate.getTime()) {
      if (field === "startDate") {
        next.endDate = new Date(parsed);
      } else {
        next.startDate = new Date(parsed);
      }
    }

    onCustomRangeChange(next);
  };

  return (
    <div className={`flex flex-col items-end gap-2 ${className}`}>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <div className={`flex items-center ${shell}`}>
          <button
            type="button"
            onClick={onPanLeft}
            disabled={!onPanLeft}
            className={`${btn} rounded-l-[14px]`}
            title="Previous period"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onPanRight}
            disabled={!onPanRight}
            className={`${btn} rounded-r-[14px]`}
            title="Next period"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {onGoToToday ? (
          <button
            type="button"
            onClick={onGoToToday}
            className={`flex items-center gap-1 px-3 py-1.5 text-sm font-semibold text-white/70 ${shell} hover:bg-surface/10`}
            title="Go to today"
          >
            <Calendar className="h-4 w-4" />
            Today
          </button>
        ) : null}

        {onGoToStart ? (
          <button
            type="button"
            onClick={onGoToStart}
            className={`px-3 py-1.5 text-sm font-semibold text-white/70 ${shell} hover:bg-surface/10`}
            title="Go to startup start date"
          >
            {startDateLabel}
          </button>
        ) : null}

        {onGoToEnd ? (
          <button
            type="button"
            onClick={onGoToEnd}
            className={`px-3 py-1.5 text-sm font-semibold text-white/70 ${shell} hover:bg-surface/10`}
            title="Go to project end date"
          >
            {endDateLabel}
          </button>
        ) : null}

        <div className={`inline-flex flex-wrap ${shell}`}>
          {zoomOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onZoomChange(option.value)}
              className={`px-3 py-1.5 text-sm font-semibold transition-colors first:rounded-l-[14px] last:rounded-r-[14px] ${
                zoomLevel === option.value
                  ? "bg-brand text-surface"
                  : "text-white/60 hover:bg-surface/10 hover:text-white"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {zoomLevel === "custom" && customRange && onCustomRangeChange ? (
        <div
          className={`flex flex-wrap items-center gap-2 px-3 py-2 text-sm ${shell}`}
        >
          <label className="flex items-center gap-2 text-white/65">
            <span className="font-medium">From</span>
            <input
              type="date"
              value={toDateInputValue(customRange.startDate)}
              onChange={(e) =>
                handleCustomDateChange("startDate", e.target.value)
              }
              className="rounded-lg border border-white/10 bg-surface-secondary px-2 py-1 text-white"
            />
          </label>
          <label className="flex items-center gap-2 text-white/65">
            <span className="font-medium">To</span>
            <input
              type="date"
              value={toDateInputValue(customRange.endDate)}
              onChange={(e) =>
                handleCustomDateChange("endDate", e.target.value)
              }
              className="rounded-lg border border-white/10 bg-surface-secondary px-2 py-1 text-white"
            />
          </label>
        </div>
      ) : null}
    </div>
  );
};

export default TimelineZoom;
